import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { QueryStatus, authAxios } from "../../utils";
import { RootState } from "../../redux/store";
import {
  CompanyFollowUp,
  CreateCompanyAcronymPayload,
  CreateCompanyExternalIbanPayload,
  DeleteCompanyPayload,
  ResetExternalBankAccountPayload,
  UpdateCompanyHasGacPayload,
  UpdateCompanyHasGeolocationPayload,
} from "./followUpApi";
import { type UpdateCompanyAutoApproveDriverAccessRequestsPayload } from "@qivia/schemas-companies";

export interface AuthorizationsState {
  companyFollowUpStatus: QueryStatus;
  createCompanyAcronymStatus: QueryStatus;
  createCompanyExternalIbanStatus: QueryStatus;
  companyFollowUp: CompanyFollowUp[];
  updateCompanyHasGacStatus: QueryStatus;
  updateCompanyAutoApproveDriverAccessRequestsStatus: QueryStatus;
  updateCompanyHasGeolocationStatus: QueryStatus;
  resetExternalBankAccountStatus: QueryStatus;
  deleteCompanyStatus: QueryStatus;
}

const initialState: AuthorizationsState = {
  companyFollowUpStatus: "idle",
  createCompanyAcronymStatus: "idle",
  createCompanyExternalIbanStatus: "idle",
  companyFollowUp: [],
  updateCompanyHasGacStatus: "idle",
  updateCompanyAutoApproveDriverAccessRequestsStatus: "idle",
  updateCompanyHasGeolocationStatus: "idle",
  resetExternalBankAccountStatus: "idle",
  deleteCompanyStatus: "idle",
};

export const companyFollowUpAsync = createAsyncThunk(
  "companyFollowUp/call",
  async () => {
    const axios = authAxios();
    const response = await axios.get<CompanyFollowUp[]>(`/companies/follow_up`);

    return response.data.sort((a, b) => (a.name < b.name ? -1 : 1));
  },
);

export const createCompanyAcronymAsync = createAsyncThunk(
  "updateCompanyAcronym/call",
  async (payload: CreateCompanyAcronymPayload) => {
    const axios = authAxios();
    await axios.post(`/company/acronym`, payload);
  },
);

export const createCompanyExternalIbanAsync = createAsyncThunk(
  "createCompanyExternalIban/call",
  async (payload: CreateCompanyExternalIbanPayload) => {
    const axios = authAxios();
    await axios.post(`/company/external_bank_account`, payload);
  },
);

export const updateCompanyHasGacAsync = createAsyncThunk(
  "updateCompanyHasGac/call",
  async (payload: UpdateCompanyHasGacPayload) => {
    const axios = authAxios();
    await axios.post(`/company/has_gac`, payload);
  },
);

export const updateCompanyAutoApproveDriverAccessRequestsAsync =
  createAsyncThunk(
    "updateCompanyAutoApproveDriverAccessRequests/call",
    async (payload: UpdateCompanyAutoApproveDriverAccessRequestsPayload) => {
      const axios = authAxios();
      await axios.post(`/company/auto_approve_driver_access_requests`, payload);
    },
  );

export const updateCompanyHasGeolocationAsync = createAsyncThunk(
  "updateCompanyHasGeolocation/call",
  async (payload: UpdateCompanyHasGeolocationPayload) => {
    const axios = authAxios();
    await axios.post(`/company/has_geolocation`, payload);
  },
);

export const resetExternalBankAccountAsync = createAsyncThunk(
  "resetExternalBankAccount/call",
  async (payload: ResetExternalBankAccountPayload) => {
    const axios = authAxios();
    await axios.post(`/company/reset_external_bank_account`, payload);
  },
);

export const deleteCompanyAsync = createAsyncThunk(
  "deleteCompany/call",
  async (payload: DeleteCompanyPayload) => {
    const axios = authAxios();
    await axios.post(`/company/delete`, payload);
  },
);

export const companyFollowUpSlice = createSlice({
  name: "companyFollowUp",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetUpdateStatus: (state) => {
      state.companyFollowUpStatus = "idle";
      state.createCompanyAcronymStatus = "idle";
    },
    resetCreateCompanyExternalIbanStatus: (state) => {
      state.createCompanyExternalIbanStatus = "idle";
    },
    resetResetExternalBankAccountStatus: (state) => {
      state.resetExternalBankAccountStatus = "idle";
    },
    resetDeleteCompanyStatus: (state) => {
      state.deleteCompanyStatus = "idle";
    },
    resetCompanyFollowUpStatus: (state) => {
      state.companyFollowUpStatus = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(companyFollowUpAsync.pending, (state) => {
        state.companyFollowUpStatus = "processing";
      })
      .addCase(companyFollowUpAsync.fulfilled, (state, action) => {
        state.companyFollowUpStatus = "success";
        state.companyFollowUp = action.payload;
      })
      .addCase(companyFollowUpAsync.rejected, (state) => {
        state.companyFollowUpStatus = "failed";
      })
      .addCase(createCompanyAcronymAsync.pending, (state) => {
        state.createCompanyAcronymStatus = "processing";
      })
      .addCase(createCompanyAcronymAsync.fulfilled, (state) => {
        state.createCompanyAcronymStatus = "success";
      })
      .addCase(createCompanyAcronymAsync.rejected, (state) => {
        state.createCompanyAcronymStatus = "failed";
      })
      .addCase(createCompanyExternalIbanAsync.pending, (state) => {
        state.createCompanyExternalIbanStatus = "processing";
      })
      .addCase(createCompanyExternalIbanAsync.fulfilled, (state) => {
        state.createCompanyExternalIbanStatus = "success";
        state.companyFollowUpStatus = "idle";
      })
      .addCase(createCompanyExternalIbanAsync.rejected, (state) => {
        state.createCompanyExternalIbanStatus = "failed";
      })
      .addCase(updateCompanyHasGacAsync.pending, (state) => {
        state.updateCompanyHasGacStatus = "processing";
      })
      .addCase(updateCompanyHasGacAsync.fulfilled, (state) => {
        state.updateCompanyHasGacStatus = "success";
      })
      .addCase(updateCompanyHasGacAsync.rejected, (state) => {
        state.updateCompanyHasGacStatus = "failed";
      })
      .addCase(
        updateCompanyAutoApproveDriverAccessRequestsAsync.pending,
        (state) => {
          state.updateCompanyAutoApproveDriverAccessRequestsStatus =
            "processing";
        },
      )
      .addCase(
        updateCompanyAutoApproveDriverAccessRequestsAsync.fulfilled,
        (state) => {
          state.updateCompanyAutoApproveDriverAccessRequestsStatus = "success";
        },
      )
      .addCase(
        updateCompanyAutoApproveDriverAccessRequestsAsync.rejected,
        (state) => {
          state.updateCompanyAutoApproveDriverAccessRequestsStatus = "failed";
        },
      )
      .addCase(updateCompanyHasGeolocationAsync.pending, (state) => {
        state.updateCompanyHasGeolocationStatus = "processing";
      })
      .addCase(updateCompanyHasGeolocationAsync.fulfilled, (state) => {
        state.updateCompanyHasGeolocationStatus = "success";
      })
      .addCase(updateCompanyHasGeolocationAsync.rejected, (state) => {
        state.updateCompanyHasGeolocationStatus = "failed";
      })
      .addCase(resetExternalBankAccountAsync.pending, (state) => {
        state.resetExternalBankAccountStatus = "processing";
      })
      .addCase(resetExternalBankAccountAsync.fulfilled, (state) => {
        state.resetExternalBankAccountStatus = "success";
      })
      .addCase(resetExternalBankAccountAsync.rejected, (state) => {
        state.resetExternalBankAccountStatus = "failed";
      })
      .addCase(deleteCompanyAsync.pending, (state) => {
        state.deleteCompanyStatus = "processing";
      })
      .addCase(deleteCompanyAsync.fulfilled, (state) => {
        state.deleteCompanyStatus = "success";
      })
      .addCase(deleteCompanyAsync.rejected, (state) => {
        state.deleteCompanyStatus = "failed";
      });
  },
});

export const selectCompanyFollowUp = (state: RootState) =>
  state.companyFollowUp.companyFollowUp;
export const selectCompanyFollowUpStatus = (state: RootState) =>
  state.companyFollowUp.companyFollowUpStatus;
export const selectCreateCompanyAcronymStatus = (state: RootState) =>
  state.companyFollowUp.createCompanyAcronymStatus;
export const selectCreateCompanyExternalIbanStatus = (state: RootState) =>
  state.companyFollowUp.createCompanyExternalIbanStatus;
export const selectUpdateCompanyHasGacStatus = (state: RootState) =>
  state.companyFollowUp.updateCompanyHasGacStatus;
export const selectUpdateCompanyAutoApproveDriverAccessRequestsStatus = (
  state: RootState,
) => state.companyFollowUp.updateCompanyAutoApproveDriverAccessRequestsStatus;
export const selectUpdateCompanyHasGeolocationStatus = (state: RootState) =>
  state.companyFollowUp.updateCompanyHasGeolocationStatus;
export const selectResetExternalBankAccountStatus = (state: RootState) =>
  state.companyFollowUp.resetExternalBankAccountStatus;
export const selectDeleteCompanyStatus = (state: RootState) =>
  state.companyFollowUp.deleteCompanyStatus;

export default companyFollowUpSlice.reducer;
